import Vue from 'vue';
import Vuex from 'vuex';

import { firebaseDefault, firebaseWebsite } from './firebase';
import 'firebase/auth';
import 'firebase/database';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    loading: false,
    artist: {},
    artists: [],
    orderBy: 'last_request_on',
    search: '',
    cities: {},
    selectedCities: [],
    selectedCitiesBkp: {},
    places: {},
    fromRegistration: false,
    completed: false,
    user: {},
    requestCookieValue: null,
  },
  getters: {
    loading: state => state.loading,
    artist: state => state.artist,
    cities: state => state.cities,
    selectedCities: state => state.selectedCities,
    selectedCitiesBkp: state => state.selectedCitiesBkp,
    orderBy: state => state.orderBy,
    search: state => state.search,
    fromRegistration: state => state.fromRegistration,
    completed: state => state.completed,
    places: state => state.places,
    place: state => (placeId) => {
      if (typeof state.places[placeId] !== 'undefined') {
        return state.places[placeId];
      }
      return null;
    },
    artists: (state) => {
      const artists = state.artists.filter(artist => artist.show);

      artists.sort((a, b) => {
        if (state.orderBy === 'name') {
          return a.name.localeCompare(b.name);
        }

        return b.lastRequestOn - a.lastRequestOn;
      });

      if (state.search === '') {
        return artists;
      }

      return artists.filter((artist) => {
        const search = state.search.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const filters = ['name', 'handle', 'place'];
        return filters.some(filter => artist[filter]
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .indexOf(search) > -1);
      });
    },
    useCookies: () => Vue.cookie.get('coal-use-cookies', { domain: process.env.VUE_APP_WEBSITE_DOMAIN }) || false,
    requestCookieValue: state => state.requestCookieValue,
    requestCookie: state => state.requestCookieValue || Vue.cookie.get('coal-request', { domain: process.env.VUE_APP_WEBSITE_DOMAIN }) || '',
    user: (state) => {
      if (Object.keys(state.user) > 0) {
        return state.user;
      }

      if (Vue.cookie.get('coal-use-cookies', { domain: process.env.VUE_APP_WEBSITE_DOMAIN }) === 'true') {
        const cookie = Vue.cookie.get('coal-user', { domain: process.env.VUE_APP_WEBSITE_DOMAIN });

        if (typeof cookie === 'string') {
          return JSON.parse(cookie);
        }
      }

      return {};
    },
    defaultArtistVar: () => ({
      requestsCount: 0,
      lastRequestOn: '1546297200000',
      requests: {},
    }),
  },
  mutations: {
    loading: (state, loading) => {
      Object.assign(state, { loading });
    },
    artist: (state, artist) => {
      Object.assign(state, { artist });
    },
    artists: (state, artists) => {
      Object.assign(state, { artists });
    },
    search: (state, search) => {
      Object.assign(state, { search });
    },
    cities: (state, cities) => {
      Object.assign(state, { cities });
    },
    selectedCities: (state, selectedCities) => {
      Object.assign(state, { selectedCities });
    },
    selectedCitiesBkp: (state, selectedCitiesBkp) => {
      Object.assign(state, { selectedCitiesBkp });
    },
    orderBy: (state, orderBy) => {
      Object.assign(state, { orderBy });
    },
    places: (state, places) => {
      Object.assign(state, { places });
    },
    fromRegistration: (state, fromRegistration) => {
      Object.assign(state, { fromRegistration });
    },
    completed: (state, completed) => {
      Object.assign(state, { completed });
    },
    useCookies: () => {
      Vue.cookie.set('coal-use-cookies', 'true', {
        domain: process.env.VUE_APP_WEBSITE_DOMAIN,
        expires: 10,
      });
    },
    requestCookieValue: (state, requestCookieValue) => {
      Object.assign(state, { requestCookieValue });
    },
    requestCookie: (state, requestCookieValue) => {
      Object.assign(state, { requestCookieValue: btoa(requestCookieValue) });
      Vue.cookie.set('coal-request', btoa(requestCookieValue), {
        domain: process.env.VUE_APP_WEBSITE_DOMAIN,
        expires: 1,
      });
    },
    user: (state, user) => {
      Object.assign(state, { user });

      if (Vue.cookie.get('coal-use-cookies', { domain: process.env.VUE_APP_WEBSITE_DOMAIN }) === 'true') {
        Vue.cookie.set('coal-user', JSON.stringify(user), {
          domain: process.env.VUE_APP_WEBSITE_DOMAIN,
          expires: 10,
        });
      }
    },
  },
  actions: {
    auth() {
      return firebaseDefault.auth().signInAnonymously();
    },
    fetchPlaces(context) {
      if (Object.keys(context.getters.places).length) {
        return new Promise(resolve => resolve());
      }

      return firebaseDefault
        .database()
        .ref('places')
        .once('value')
        .then((response) => {
          context.commit('places', response.val());
        });
    },
    fetchArtists(context) {
      const artists = [];

      return firebaseDefault
        .database()
        .ref('artists')
        .once('value')
        .then((response) => {
          response.forEach((childSnapshot) => {
            const artist = childSnapshot.val();
            artist.id = childSnapshot.key;
            artist.picture = `${process.env.VUE_APP_STORAGE_BUCKET}/artists/artist_${artist.id}.jpg`;

            if (typeof artist.name !== 'string') {
              artist.name = artist.name.toString();
            }

            if (typeof artist.handle !== 'string') {
              artist.handle = artist.handle.toString();
            }

            if (artist.show && context.getters.place(artist.placeId)) {
              artist.place = context.getters.place(artist.placeId);
              artists.push(artist);
            }
          });

          context.commit('artists', artists);
        });
    },
    fetchArtist(context, handle) {
      return firebaseDefault
        .database()
        .ref('artists')
        .orderByChild('handle')
        .equalTo(handle)
        .limitToFirst(1)
        .once('value')
        .then((response) => {
          let artist = {};
          const responseVal = response.val();

          if (responseVal) {
            Object.keys(responseVal)
              .forEach((key) => {
                artist = responseVal[key];
                artist.id = parseInt(key, 10);
                artist.picture = `${process.env.VUE_APP_STORAGE_BUCKET}/artists/artist_${artist.id}.jpg`;

                if (typeof artist.name !== 'string') {
                  artist.name = artist.name.toString();
                }

                if (typeof artist.handle !== 'string') {
                  artist.handle = artist.handle.toString();
                }

                if (artist.show && context.getters.place(artist.placeId)) {
                  artist.place = context.getters.place(artist.placeId);
                } else {
                  artist.show = false;
                }
              });
          }
          context.commit('artist', artist);
        });
    },
    fetchArtistVariables(context, artistId) {
      return firebaseDefault
        .database()
        .ref(`artistsVariables/${artistId}`)
        .once('value');
    },
    fetchByArtist(context, { artistId, entity }) {
      return firebaseDefault
        .database()
        .ref(entity)
        .orderByChild('artistId')
        .equalTo(artistId)
        .once('value');
    },
    fetchTravels(context, artistId) {
      return firebaseDefault
        .database()
        .ref('travels')
        .orderByChild('artistId')
        .equalTo(artistId)
        .once('value');
    },
    fetchRequests(context, artistId) {
      return firebaseDefault
        .database()
        .ref(`cities/${artistId}`)
        .once('value');
    },
    fillInPlace(context, entity) {
      if (typeof entity.placeId !== 'undefined' && context.getters.place(entity.placeId)) {
        Object.assign(entity, { place: context.getters.place(entity.placeId) });
        return true;
      }

      return false;
    },
    checkMaxRequests(context, email) {
      return firebaseDefault
        .database()
        .ref('requests')
        .orderByChild('email')
        .equalTo(email)
        .once('value');
    },
    saveUserInWebsite(context) {
      return firebaseWebsite.auth().signInAnonymously().then(() => {
        const { profile } = context.getters.user;

        firebaseWebsite
          .database()
          .ref('/users_prelaunch')
          .orderByChild('email')
          .equalTo(context.getters.user.email)
          .once('value')
          .then((snapshot) => {
            if (!snapshot.exists()) {
              firebaseWebsite
                .database()
                .ref('/users_prelaunch')
                .push()
                .set({
                  lang: navigator.language,
                  contact_type: `tattoo${profile.charAt(0).toUpperCase()}${profile.slice(1)}`,
                  email: context.getters.user.email,
                  source: 'ctmc',
                });
            }
          });
      });
    },
    saveRequests(context, { artistId, requests }) {
      const promises = [];
      context.dispatch('fetchArtistVariables', artistId).then((response) => {
        const variables = response.val() || {
          lastRequestOn: 0,
          requests: {},
          requestsCount: 0,
        };

        Object.keys(context.getters.places).forEach((placeId) => {
          const promise = firebaseDefault
            .database()
            .ref(`places/${placeId}`)
            .set(context.getters.places[placeId]);

          promises.push(promise);
        });

        requests.forEach((request) => {
          if (typeof variables.requests[request.placeId] !== 'undefined') {
            variables.requests[request.placeId] += 1;
          } else {
            variables.requests[request.placeId] = 1;
          }

          const promise = firebaseDefault
            .database()
            .ref('requests')
            .push()
            .set(request);

          promises.push(promise);
        });

        variables.lastRequestOn = (new Date()).getTime();
        variables.requestsCount += requests.length;

        const promise = firebaseDefault
          .database()
          .ref(`artistsVariables/${artistId}`)
          .set(variables);

        promises.push(promise);
        promises.push(context.dispatch('saveUserInWebsite'));
      });

      return Promise.all(promises);
    },
  },
});
